import React from 'react';
import { Controller, Control, FieldErrors, FieldValues, Path, PathValue } from 'react-hook-form';
import { Form, Button } from 'antd';
import { SaveOutlined } from '@ant-design/icons';

// Generic field definition for form fields
export interface FormField<T extends FieldValues = any> {
  name: Path<T>;
  label: string;
  required: boolean;
  errorMsg?: string;
  rules?: Record<string, any>;
  render: (field: any) => React.ReactElement;
}

export interface FormBuilderProps<T extends FieldValues = any> {
  formFields: FormField<T>[];
  control: Control<T>;
  errors: FieldErrors<T>;
  onSubmit: () => void;
  submitButtonText?: string;
  isLoading?: boolean;
  columns?: 1 | 2 | 3 | 4;
  showSubmitButton?: boolean;
}

const FormBuilder = <T extends FieldValues>({
  formFields,
  control,
  errors,
  onSubmit,
  submitButtonText = 'Submit',
  isLoading = false,
  columns = 2,
  showSubmitButton = true,
}: FormBuilderProps<T>): React.ReactElement => {
  const getColumnClass = (): string => {
    switch (columns) {
      case 1:
        return 'col-md-12';
      case 2:
        return 'col-md-6';
      case 3:
        return 'col-md-4';
      case 4:
        return 'col-md-3';
      default:
        return 'col-md-6';
    }
  };

  return (
    <div className="row">
      <Form layout="vertical" onFinish={onSubmit}>
        <div className="row">
          {formFields.map((field) => (
            <div key={String(field.name)} className={getColumnClass()}>
              <Form.Item
                label={field.label}
                validateStatus={field.errorMsg ? 'error' : undefined}
                help={field.errorMsg}
              >
                <Controller
                  name={field.name}
                  control={control}
                  rules={field.rules}
                  render={({ field: fieldProps }) => field.render(fieldProps)}
                />
              </Form.Item>
            </div>
          ))}
        </div>

        {showSubmitButton && (
          <Form.Item style={{ textAlign: 'right' }}>
            <Button
              size="large"
              disabled={isLoading}
              icon={<SaveOutlined />}
              type="primary"
              htmlType="submit"
              className="shadow-lg"
              loading={isLoading}
            >
              {submitButtonText}
            </Button>
          </Form.Item>
        )}
      </Form>
    </div>
  );
};

export default FormBuilder;
